import * as React from 'react';

import type { Preloadable } from './lazyWithPreload';

/**
 * Yield the main thread back to the browser between two units of work.
 *
 * `requestIdleCallback` alone isn't enough here: a task inside it still
 * blocks the main thread until it finishes, so an interaction that arrives
 * mid-preload has to wait (see
 * https://loke.dev/blog/what-nobody-tells-you-about-requestidlecallback-inp/).
 * Prefers the native `scheduler.yield()` since it re-enters at user-visible
 * priority instead of the back of the task queue; falls back to
 * `setTimeout(0)` where it's unavailable (e.g. Safari). Only bounds
 * *contiguous* main-thread blockage — a single large chunk's own module
 * evaluation still can't be interrupted mid-way. Kept local to this hook
 * rather than folded into `preload()` itself: `Screens/routePreload.ts` also
 * calls `preload()` for the current route's boot-critical chunks, which must
 * fire without yielding.
 */
function yieldToMain(): Promise<void> {
    const nativeScheduler = (globalThis as { scheduler?: { yield?: () => Promise<void> } })
        .scheduler;
    if (typeof nativeScheduler?.yield === 'function') {
        return nativeScheduler.yield();
    }
    return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Whether the browser has detected input that hasn't been processed yet
 * (e.g. a click or keypress). Falls back to `false` where the API is
 * unavailable (only Chromium supports it today), which just means we don't
 * get the extra signal — `yieldToMain` between neighbors still applies.
 */
function isInputPending(): boolean {
    const scheduling = (navigator as unknown as { scheduling?: { isInputPending?: () => boolean } })
        .scheduling;
    return scheduling?.isInputPending?.() ?? false;
}

/**
 * Preload a set of lazily-loaded components once the page has finished
 * loading and the browser is idle.
 *
 * Used to warm the chunks of screens the user is likely to visit next (a
 * "bunch") after the current screen has settled, so the follow-up navigation
 * renders without waiting on the network. Preloading waits for `window.load`
 * so it never competes on the network with the current route's own chunk and
 * data fetches (see `Screens/routePreload.ts`), then yields to the main
 * thread between each neighbor so their chunk evaluations don't stack into
 * one long task. Compare with `useIsBrowserIdle`, which is render-driven and
 * doesn't wait for `load` — not reused here because it doesn't give this hook
 * a hook into cancellation between neighbors.
 */
export function useNeighborPreload(neighbors: Preloadable[]): void {
    React.useEffect(() => {
        if (neighbors.length === 0) {
            return;
        }

        let cancelled = false;
        let idleId: number | undefined;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const preloadAll = async () => {
            for (const neighbor of neighbors) {
                // Don't start the next chunk while the browser has unprocessed
                // input (e.g. a click) — wait it out first so preloading never
                // delays the response to an actual interaction.
                while (isInputPending() && !cancelled) {
                    await yieldToMain();
                }
                if (cancelled) {
                    return;
                }
                // Fire-and-forget per neighbor: `preload` memoizes, so a
                // failed or repeated call is harmless and the component
                // still loads on render.
                await neighbor.preload().catch(() => {});
                await yieldToMain();
            }
        };

        const scheduleIdle = () => {
            if (cancelled) {
                return;
            }
            if (typeof requestIdleCallback === 'function') {
                idleId = requestIdleCallback(() => void preloadAll(), { timeout: 3000 });
            } else {
                // No requestIdleCallback (e.g. Safari): wait longer than the
                // rIC timeout above so we don't guess at "idle" too eagerly
                // and end up competing with the current screen's own load.
                timeoutId = setTimeout(() => void preloadAll(), 2000);
            }
        };

        const onLoad = () => scheduleIdle();

        if (document.readyState === 'complete') {
            scheduleIdle();
        } else {
            window.addEventListener('load', onLoad, { once: true });
        }

        return () => {
            cancelled = true;
            window.removeEventListener('load', onLoad);
            if (idleId !== undefined && typeof cancelIdleCallback === 'function') {
                cancelIdleCallback(idleId);
            }
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        };
        // The neighbors array is rebuilt per render; depend on its identity via
        // the resolved list length + members so the effect re-runs on screen change.
    }, [neighbors]);
}
